// Shrinking a photo before it leaves the phone.
//
// A modern phone camera produces 4–12MB per shot. A ten-photo prestart is
// therefore up to 120MB over what is often a single bar of signal at the back
// of a site — which is not a slow upload, it is a failed one.
//
// 1600px on the long edge at quality 0.8 lands around 200–400KB and is still
// enough to see a cracked weld or a torn hose, which is what these photos are
// for. Anything smaller starts losing the detail somebody took the photo to
// record.
//
// Done on the CLIENT rather than the server on purpose: the bytes that never
// leave the phone are the ones that cannot fail to arrive.

const MAX_EDGE = 1600;
const QUALITY = 0.8;

/**
 * Returns a compressed JPEG, or the original file if anything at all goes
 * wrong.
 *
 * The fallback is the important part. A browser that cannot decode this
 * particular image, an OffscreenCanvas that is not available, a photo in a
 * format canvas will not take — none of those should cost somebody the photo.
 * A large upload is a worse outcome than a small one and a much better outcome
 * than no upload.
 */
export async function compressImage(file: File): Promise<Blob> {
  try {
    if (!file.type.startsWith('image/')) return file;

    const bitmap = await createImageBitmap(file);
    const scale = Math.min(1, MAX_EDGE / Math.max(bitmap.width, bitmap.height));

    // Already small enough. Re-encoding would only lose quality.
    if (scale === 1 && file.size < 600_000) {
      bitmap.close();
      return file;
    }

    const width = Math.round(bitmap.width * scale);
    const height = Math.round(bitmap.height * scale);

    const canvas = document.createElement('canvas');
    canvas.width = width;
    canvas.height = height;
    const context = canvas.getContext('2d');
    if (!context) {
      bitmap.close();
      return file;
    }
    context.drawImage(bitmap, 0, 0, width, height);
    bitmap.close();

    const blob = await new Promise<Blob | null>((resolve) =>
      canvas.toBlob(resolve, 'image/jpeg', QUALITY)
    );
    // If the "compressed" version is somehow bigger, keep the original.
    return blob && blob.size < file.size ? blob : file;
  } catch {
    return file;
  }
}
